import {
  DataTable,
  selectColumn,
  createSortableColumn,
  createCustomColumn,
  AbbreviateTableColumn,
  getFullName,
  createActionsColumn,
  formatLearningPathName,
  formatDateToDutch,
  MultiLevelDropdownMenu,
} from "toptaal-shared"
import {
  createFileRoute,
  useRouter,
  useNavigate,
  useSearch,
} from "@tanstack/react-router"
import { useState } from "react"
import ScreenWrapper from "@/components/ScreenWrapper"
import { TABLE_PAGE_SIZE } from "toptaal-shared"
import { checkPermissions } from "@/utils/checkPermissions"
import {
  usePaginatedTableWithUrlState,
  createTableSearchSchema,
  useExportMutation,
} from "toptaal-shared"
import { Group } from "toptaal-shared"
import type { ColumnDef } from "@tanstack/react-table"
import { TableHeaderActions } from "@/components/data-table/TableHeaderActions"
import { groupQueryKeys } from "toptaal-shared"
import ChangePlannerModal from "@/components/modals/ChangePlannerModal"
import { DeleteGroupModal } from "@/components/modals/group/DeleteGroupModal"
import AddStudentToGroupModal from "@/components/modals/group/AddStudentToGroupModal"
import {
  GROUP_STORAGE_KEY,
  useGroupFilters,
} from "@/hooks/filters/useGroupFilters"

export const Route = createFileRoute("/beheer/groepen/")({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "groups"),
  validateSearch: createTableSearchSchema(),
})

const DEFAULT_COLUMN_VISIBILITY = {
  teachers: false,
  lessonStartDate: false,
}

const ALL_COLUMN_IDS = [
  "id",
  "project_names",
  "learning_path_name",
  "students",
  "capacity",
  "planners",
  "locations",
  "teachers",
  "lessonStartDate",
]

function RouteComponent() {
  const router = useRouter()
  const searchParams = useSearch({ from: "/beheer/groepen/" })
  const navigate = useNavigate({
    from: "/beheer/groepen/",
  })

  const { filterItems, activeFilters, queryFn } = useGroupFilters({
    onPageReset: () =>
      navigate({ search: (prev) => ({ ...prev, page: 1 }), replace: true }),
  })

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
    resetColumnVisibility,
    mergedFilters,
    selectedColumns,
    rowSelection,
    setRowSelection,
    selectedIds,
    queryKeyMerged,
  } = usePaginatedTableWithUrlState({
    queryKey: [...groupQueryKeys.all, activeFilters],
    queryFn,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    storageKey: GROUP_STORAGE_KEY,
    defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
    allColumnIds: ALL_COLUMN_IDS,
  })

  const [isAddStudentOpen, setIsAddStudentOpen] = useState(false)
  const [changePlannerModalOpen, setChangePlannerModalOpen] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  const handleNavigateDetail = (groupId: Group["id"]) => {
    router.navigate({
      to: "/beheer/groepen/$groupId/gegevens",
      params: { groupId: groupId.toString() },
      search: { from: router.state.location.href },
    })
  }

  const columns: ColumnDef<Group>[] = [
    selectColumn(),
    createSortableColumn({ accessorKey: "id", name: "ID" }),
    createCustomColumn({
      id: "project_names",
      name: "Contract",
      cell: ({ row }) => (
        <AbbreviateTableColumn
          entities={row.original.projects.map((p) => ({ name: p.name }))}
          cutOffCount={1}
        />
      ),
      accessorKey: "projects",
    }),
    createCustomColumn({
      id: "learning_path_name",
      name: "Leerlijn",
      cell: ({ row }) => (
        <AbbreviateTableColumn
          entities={row.original.learningPaths
            .map((lp) =>
              formatLearningPathName({
                learningPath: lp,
                moduleCode: row.original.module?.moduleCode,
              }),
            )
            .filter(Boolean)
            .map((name) => ({ name: name! }))}
          cutOffCount={1}
        />
      ),
      accessorKey: "learningPaths",
    }),
    createCustomColumn({
      id: "students",
      name: "Cursisten",
      cell: ({ row }) => `${row.original.studentsCount ?? 0} cursisten`,
      accessorKey: "studentsCount",
    }),
    createCustomColumn({
      id: "capacity",
      name: "Maximale groepsgrootte",
      cell: ({ row }) => `${row.original.capacity}`,
    }),
    createCustomColumn({
      id: "planners",
      name: "Planners",
      cell: ({ row }) => (
        <AbbreviateTableColumn
          entities={row.original.planners.map((planner) => {
            return {
              name: getFullName({
                firstName: planner.firstName,
                lastName: planner.lastName,
              }),
            }
          })}
        />
      ),
      accessorKey: "planners",
    }),
    createCustomColumn({
      id: "locations",
      name: "Leslocaties",
      cell: ({ row }) => (
        <AbbreviateTableColumn
          entities={row.original.locations.map((location) => {
            return {
              name: location.name,
            }
          })}
        />
      ),
      accessorKey: "locations",
    }),
    createCustomColumn({
      id: "teachers",
      name: "Docenten",
      cell: ({ row }) =>
        AbbreviateTableColumn({
          entities:
            row.original?.teachers?.map((teacher) => {
              return {
                name: getFullName({
                  firstName: teacher.firstName,
                  lastName: teacher.lastName,
                }),
              }
            }) || [],
        }),
      accessorKey: "teachers",
    }),
    createCustomColumn({
      id: "lessonStartDate",
      name: "Startdatum",
      cell: ({ row }) => {
        const startDate = row.original.lessonStartDate

        return startDate ? formatDateToDutch(startDate) : "-"
      },
      accessorKey: "lessonStartDate",
    }),
    createActionsColumn([
      {
        label: "Cursist toevoegen",
        onClick: (e, group) => {
          e.stopPropagation()
          setSelectedGroup(group)
          setIsAddStudentOpen(true)
        },
      },
      {
        label: "Planner wijzigen",
        onClick: (e, group) => {
          e.stopPropagation()
          setSelectedGroup(group)
          setChangePlannerModalOpen(true)
        },
      },
      {
        label: "Groep verwijderen",
        onClick: (e, group) => {
          e.stopPropagation()
          setSelectedGroup(group)
          setIsDeleteOpen(true)
        },
      },
    ]),
  ]

  const { mutate: exportGroups } = useExportMutation({
    exportApiPrefix: "lesson_groups",
    filters: mergedFilters,
    selectedColumns,
    selectedIds,
    activeFilters,
  })

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        title="Groepen"
        data={data?.data.lessonGroups || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        onRowClick={handleNavigateDetail}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        resetColumnVisibility={resetColumnVisibility}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        columns={columns}
        headerLeftActions={<MultiLevelDropdownMenu items={filterItems} />}
        headerRightActions={
          <>
            <TableHeaderActions
              exportAction={{
                onClick: exportGroups,
              }}
            />

            <TableHeaderActions
              createAction={{
                label: "Nieuwe groep",
                to: "/beheer/groepen/aanmaken",
                entity: "groups",
                permissions: "create",
              }}
            />
          </>
        }
      />

      {isAddStudentOpen && selectedGroup && (
        <AddStudentToGroupModal
          isOpen={isAddStudentOpen}
          onClose={() => {
            setIsAddStudentOpen(false)
            setSelectedGroup(null)
          }}
          group={selectedGroup}
          mergedFilters={mergedFilters}
        />
      )}

      {selectedGroup && (
        <ChangePlannerModal
          isOpen={changePlannerModalOpen}
          onClose={() => {
            setChangePlannerModalOpen(false)
            setSelectedGroup(null)
          }}
          group={selectedGroup}
          mergedFilters={mergedFilters}
        />
      )}

      {isDeleteOpen && selectedGroup && (
        <DeleteGroupModal
          isOpen={isDeleteOpen}
          setIsOpen={setIsDeleteOpen}
          group={selectedGroup}
          queryKey={queryKeyMerged}
        />
      )}
    </ScreenWrapper>
  )
}
